// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCompanyAssets, updateFavicon } from '../utils/companyAssets';

type SvgImport = string | React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface ClientConfig {
  companyName: string;
  callbackUri: string;
}

interface ConfigContextType {
  config: ClientConfig | null;
  isLoading: boolean;
  logo: SvgImport;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logo, setLogo] = useState<SvgImport>('');

  useEffect(() => {
    const fetchConfig = async (): Promise<void> => {
      try {
        const response = await fetch('/api/config');
        const data: ClientConfig = await response.json();
        setConfig(data);

        // Get company-specific assets
        const assets = getCompanyAssets(data.companyName);
        setLogo(assets.logo);
        updateFavicon(assets.favicon);
      } catch (error) {
        console.error('Failed to fetch client config:', error);
        // Fallback to default values
        const defaultConfig = {
          companyName: 'G42',
          callbackUri: ''
        };
        setConfig(defaultConfig);

        // Set default assets
        const assets = getCompanyAssets('G42');
        setLogo(assets.logo);
        updateFavicon(assets.favicon);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return <ConfigContext.Provider value={{ config, isLoading, logo }}>{children}</ConfigContext.Provider>;
};
